import { AlertTriangle, RefreshCw } from 'lucide-react'
import type { ErrorInfo, ReactNode } from 'react'
import { Component } from 'react'

import { Button } from '@/components/ui/button'
import i18n from '@/i18n'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo)
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div className="flex min-h-[400px] flex-col items-center justify-center p-8">
          <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-8 text-center">
            <AlertTriangle className="mx-auto h-12 w-12 text-destructive" />
            <h2 className="mt-4 text-xl font-semibold text-foreground">
              {i18n.t('errors.somethingWentWrong')}
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              {this.state.error?.message || i18n.t('errors.unexpectedErrorOccurred')}
            </p>
            <Button onClick={this.handleRetry} className="mt-4" variant="outline">
              <RefreshCw className="mr-2 h-4 w-4" />
              {i18n.t('errors.tryAgain')}
            </Button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
